import React, { useRef } from 'react';
import { Dropdown, MenuProps, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faICursor, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { IOCRParagraph } from 'interfaces';
import classes from './OCRTextView.module.scss';

type OCRTextViewProps = {
  paragraphs: IOCRParagraph[];
  imageWidth?: number | null;
};

const OCRTextView = ({ paragraphs, imageWidth }: OCRTextViewProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<string>('');

  const handleContextMenu = () => {
    savedSelectionRef.current = window.getSelection()?.toString() ?? '';
  };

  const handleCopy = () => {
    const text = savedSelectionRef.current;
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      message.success(t('Documents.ocr.copyText'));
    });
  };

  const handleSelectAll = () => {
    if (!containerRef.current) return;
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(containerRef.current);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'copy',
      label: (
        <span className={classes.spanItem}>
          <FontAwesomeIcon className={classes.faIcon} icon={faCopy} />
          {t('Documents.ocr.copyText')}
        </span>
      ),
      onClick: handleCopy,
      className: classes.menuItem,
    },
    {
      type: 'divider',
      className: classes.divider,
    },
    {
      key: 'selectAll',
      label: (
        <span className={classes.spanItem}>
          <FontAwesomeIcon className={classes.faIcon} icon={faICursor} />
          {t('Documents.ocr.selectAll')}
        </span>
      ),
      onClick: handleSelectAll,
      className: classes.menuItem,
    },
  ];

  const hasRecognizedText = paragraphs.some((item) => item.paragraph && /\S/.test(item.paragraph));

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['contextMenu']}
      classNames={{ root: classes.contextMenu }}
    >
      <div
        ref={containerRef}
        className={`${classes.container} ${!hasRecognizedText ? classes.emptyContainer : ''}`}
        onContextMenu={handleContextMenu}
        style={{ '--ocr-image-width': imageWidth ? `${imageWidth}px` : '700px' } as React.CSSProperties}
      >
        {hasRecognizedText ? (
          paragraphs.map((item, index) => (
            <p key={index} className={classes.paragraph}>
               {item.paragraph}
            </p>
          ))
        ) : (
          <div className={classes.fallbackText}>
            <div className={classes.fallbackIconWrapper}>
              <FontAwesomeIcon icon={faEyeSlash} className={classes.fallbackIcon} />
            </div>
            <h3 className={classes.fallbackTitle}>
              {t('Documents.ocr.notRecognized')}
            </h3>
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default OCRTextView;
